import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  FC,
  ReactNode,
} from 'react';

interface SubThreadViewContextType {
  isOpen: boolean;
  openSubThreadView: () => void;
  closeSubThreadView: () => void;
  toggleSubThreadView: () => void;
}

const SubThreadViewContext = createContext<SubThreadViewContextType | null>(
  null,
);

interface SubThreadViewProviderProps {
  children: ReactNode;
}

export const SubThreadViewProvider: FC<SubThreadViewProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const openSubThreadView = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeSubThreadView = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleSubThreadView = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  // Only the components that actually use isOpen will re-render
  const contextValue = useMemo(
    () => ({
      isOpen,
      openSubThreadView,
      closeSubThreadView,
      toggleSubThreadView,
    }),
    [isOpen, openSubThreadView, closeSubThreadView, toggleSubThreadView],
  );

  return (
    <SubThreadViewContext.Provider value={contextValue}>
      {children}
    </SubThreadViewContext.Provider>
  );
};

export const useSubThreadView = (): SubThreadViewContextType => {
  const context = useContext(SubThreadViewContext);
  if (!context) {
    throw new Error(
      'useSubThreadView must be used within a SubThreadViewProvider',
    );
  }
  return context;
};

// Hook that only returns the toggle function - won't cause re-renders on isOpen change
export const useSubThreadViewActions = () => {
  const context = useContext(SubThreadViewContext);
  if (!context) {
    throw new Error(
      'useSubThreadViewActions must be used within a SubThreadViewProvider',
    );
  }
  return {
    openSubThreadView: context.openSubThreadView,
    closeSubThreadView: context.closeSubThreadView,
    toggleSubThreadView: context.toggleSubThreadView,
  };
};

// Hook that only returns the isOpen state
export const useSubThreadViewIsOpen = () => {
  const context = useContext(SubThreadViewContext);
  if (!context) {
    throw new Error(
      'useSubThreadViewIsOpen must be used within a SubThreadViewProvider',
    );
  }
  return context.isOpen;
};
